import { useState } from "react";
import "./MyOrganizationPage.css";
import { useAuth } from "../../hooks/useAuth";
import { useEntitlements } from "../../hooks/useEntitlements";
import { updateOrganizationAsync } from "../../api/OrganizationApi";
import { Constants } from "../../constants/constants";
import { OrgForms } from "../../config/forms";
import OrganizationModal, {
  OrganizationFormValues,
  toUpdateOrganizationRequest,
} from "../platform/organizations/OrganizationCreateModal";
import SubscriptionInfoCard from "../../components/SubscriptionInfoCard";
import Toast from "../../components/Toast";

const MyOrganizationPage = () => {
  const { user, hasPrivilege, refreshUser } = useAuth();
  const { entitlements, loading, refresh } = useEntitlements();
  const org = user?.activeOrganization ?? null;
  const canEdit = hasPrivilege(OrgForms.Dashboard, "canEdit");

  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
  } | null>(null);

  const handleSubmit = async (form: OrganizationFormValues) => {
    if (!org) return;
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    setSubmitting(true);
    try {
      const res = await updateOrganizationAsync(
        token,
        org.id,
        toUpdateOrganizationRequest(form, org),
      );
      if (res.success && res.result) {
        setModalOpen(false);
        setToast({
          message: "Organization updated successfully!",
          type: "success",
        });
        refreshUser();
      } else {
        setToast({
          message: res.message || "Failed to update organization",
          type: "error",
        });
      }
    } catch (err) {
      console.error(err);
      setToast({
        message: "Failed to update organization. Please try again.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const fields: {
    label: string;
    value: string | null | undefined;
    full?: boolean;
  }[] = org
    ? [
        { label: "Email", value: org.email },
        { label: "Phone", value: org.phone },
        { label: "City", value: org.city },
        { label: "Area", value: org.area },
        { label: "Address", value: org.address },
        { label: "Website Title", value: org.websiteTitle },
        { label: "Website Tagline", value: org.websiteTagline },
        { label: "Description", value: org.description, full: true },
        { label: "Facebook", value: org.facebookUrl },
        { label: "Instagram", value: org.instagramUrl },
        { label: "YouTube", value: org.youtubeUrl },
        { label: "LinkedIn", value: org.linkedInUrl },
        { label: "TikTok", value: org.tiktokUrl },
        { label: "Google Maps", value: org.googleMapsUrl },
      ].filter((f) => f.value)
    : [];

  return (
    <div className="my-org-page">
      <div className="my-org-header">
        <div>
          <h2>My Organization</h2>
          <p>View and update your organization's details.</p>
        </div>
        {canEdit && (
          <button
            className="my-org-btn my-org-btn--primary"
            onClick={() => setModalOpen(true)}
          >
            Edit Organization
          </button>
        )}
      </div>

      {!org ? (
        <div className="my-org-empty">
          No organization is assigned to your account.
        </div>
      ) : (
        <div className="my-org-card">
          <div className="my-org-card__heading">
            <span className="my-org-card__avatar">
              {org.name.charAt(0).toUpperCase()}
            </span>
            <div>
              <h3>{org.name}</h3>
              {org.slug && <p className="my-org-card__slug">/{org.slug}</p>}
            </div>
          </div>

          {fields.length > 0 ? (
            <dl className="my-org-fields">
              {fields.map((field) => (
                <div
                  className={`my-org-field${field.full ? " my-org-field--full" : ""}`}
                  key={field.label}
                >
                  <dt className="my-org-field__label">{field.label}</dt>
                  <dd className="my-org-field__value">{field.value}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="my-org-empty">No organization details available.</p>
          )}
        </div>
      )}

      {org && (
        <div className="my-org-subscription">
          <SubscriptionInfoCard
            entitlements={entitlements}
            loading={loading}
            onRefresh={refresh}
          />
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {org && (
        <OrganizationModal
          isOpen={modalOpen}
          mode="edit"
          organization={org}
          submitting={submitting}
          onClose={() => setModalOpen(false)}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
};

export default MyOrganizationPage;
